import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import Button from 'components/ui/button';
import Form from 'components/ui/form';
import ModalTitle from 'components/modal/components/ModalTitle';
import ModalContent from 'components/modal/components/ModalContent';
import ModalFooter from 'components/modal/components/ModalFooter';
import { addTranslation, IntlProps } from 'decorators/addTranslation';
import { StoreProps } from 'store';
import './style.scss';
import CustomSelect from 'components/ui/customSelect/CustomSelect';
import SelectItem from 'types/SelectItem';
import Loader from 'components/ui/loader';
import { loadDictionary } from 'api/dictionaries';
import InputAmount from 'components/ui/inputAmount';
import { openModal } from 'actions/modal';
import { createMoneyTransfer, MoneyTransferProps } from 'api/balanceManage';
import showNotification from 'components/ui/notification/showNotification';
import Input from 'components/ui/input/Input';
import Utils from 'helpers/Utils';

interface TransferMoneyFormInterface {
  balanceId: SelectItem | null;
  balanceTo: SelectItem | null;
  amount: number | null;
  currency: string | null;
}

interface BillingBalance {
  balance: number;
  currency: string;
  id: number;
  title: string;
  balanceGroupId: number | null;
}

type Props = StoreProps & IntlProps;

const PER_PAGE = 20;

const TransferMoney = (props: Props) => {
  const {
    getTranslate,
    dispatch,
  } = props;

  const [billingBalances, setBillingBalances] = useState<BillingBalance[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [offset, setOffset] = useState<number>(0);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<TransferMoneyFormInterface>({
    balanceId: null,
    amount: null,
    balanceTo: null,
    currency: null,
  });

  const loadBillingBalances = useCallback(async () => {
    setIsLoading(true);
    const { hasMoreRows, elements } = await loadDictionary({ name: 'billingBalance', offset }, false);

    setBillingBalances((prev) => {
      const newElements = elements.filter(
        (el) => !prev.some((existing) => existing.id === el.id)
      );
      return [...prev, ...newElements];
    });

    if (hasMoreRows) {
      setOffset(offset + PER_PAGE);
    }
    setIsLoading(false);
  }, [setBillingBalances, setIsLoading, setOffset, offset]);

  const balanceToOptions: SelectItem[] = useMemo(() => {
    let filtered = billingBalances;
    if (formData.balanceId) {
      const balanceFrom = billingBalances.find(({ id }) => id === formData.balanceId?.value);
      if (balanceFrom && balanceFrom.balanceGroupId !== null) {
        filtered = billingBalances.filter(
          ({ id, balanceGroupId }) => id !== balanceFrom.id && balanceGroupId === balanceFrom.balanceGroupId
        );
      }
    }
    return filtered.map((balance): SelectItem => ({
      value: balance.id,
      label: `${balance.title} - ${balance.currency}`,
      currency: balance.currency,
    }));
  }, [billingBalances, formData.balanceId]);

  const handleChange = (field: keyof TransferMoneyFormInterface) => (value: any) => {
    const newValue = value?.target !== undefined ? value.target.value : value;
    setFormData((prev) => ({ ...prev, [field]: newValue }));
  };

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      currency: formData.balanceId?.currency || '',
      balanceTo: null
    }));
  }, [formData.balanceId, setFormData]);

  const items: SelectItem[] = useMemo(() => {
    return billingBalances.map((balance): SelectItem => {
      return {
        value: balance.id,
        label: `${balance.title} - ${balance.currency}`,
        currency: balance.currency,
      };
    });
  }, [billingBalances]);

  const sanitizeNumber = (value: string | number | undefined) => value
    ? String(value).replace(/\s/g, '')
    : value;

  const handleSubmit = useCallback(async (e?: React.FormEvent) => {
    e?.preventDefault();
    dispatch(
      openModal({
        modalId: 'Confirm',
        content: {
          title: 'balanceManage.modal.transferMoney',
          text: Utils.sprintf(
            getTranslate('balanceManage.modal.confirm.message'),
            formData.amount,
            formData.currency,
            formData.balanceId?.label || '',
            formData.balanceTo?.label || ''
          ),
        },
        callback: (isAgree) => {
          if (isAgree) {
            const params: MoneyTransferProps = {
              balanceId: formData.balanceId?.value || 0,
              balanceTo: formData.balanceTo?.value || 0,
              amount: Number(sanitizeNumber(formData.amount || 0)),
              currency: formData.currency || '',
            };
            setIsLoading(true);
            try {
              createMoneyTransfer(params);
            } catch (error: any) {
              if (error.payload?.validationErrors) {
                setValidationErrors(error.payload.validationErrors);
              } else {
                showNotification({ status: 'error', content: error.payload });
              }
            } finally {
              setIsLoading(false);
            }
          }
        },
      }),
    );
  }, [dispatch, formData, setIsLoading, getTranslate]);

  return (
    <div className="balanceManage-transferMoney-modal">
      {isLoading && <Loader />}
      <ModalTitle>
        {getTranslate('balanceManage.modal.transferMoney')}
      </ModalTitle>

      <ModalContent>
        <Form onSubmit={handleSubmit}>
          <div className="transferMoney-form__content">
            <div className="transferMoney-form__item">
              <CustomSelect
                id="balanceId"
                label={getTranslate('balanceManage.modal.balanceFrom.label')}
                isSearchable={true}
                isLoading={isLoading}
                options={items}
                value={formData.balanceId}
                onChange={handleChange('balanceId')}
                error={validationErrors?.balanceId}
                isLoadMore={true}
                loadMore={loadBillingBalances}
                onOpen={loadBillingBalances}
                modern
                isRequired
              />
            </div>
            <div className="transferMoney-form__item">
              <InputAmount
                label={getTranslate('balanceManage.modal.amount.label')}
                id="input-amount"
                placeholder="0.00"
                value={formData.amount || ''}
                onChange={handleChange('amount')}
                modern
                error={validationErrors?.amount}
                fractional={6}
                allowNegative={false}
                isRequired
              />
            </div>
            <div className="transferMoney-form__item">
              <CustomSelect
                id="balanceTo"
                label={getTranslate('balanceManage.modal.balanceTo.label')}
                isSearchable={true}
                isLoading={isLoading}
                options={balanceToOptions}
                value={formData.balanceTo}
                onChange={handleChange('balanceTo')}
                error={validationErrors?.balanceTo}
                isLoadMore={true}
                loadMore={loadBillingBalances}
                onOpen={loadBillingBalances}
                modern
                isRequired
              />
            </div>
            <div className="transferMoney-form__item">
              <Input
                id="currency"
                label={getTranslate('balanceManage.modal.currency.label')}
                value={formData.currency || ''}
                placeholder={getTranslate('balanceManage.modal.currency.label')}
                onChange={handleChange('currency')}
                error={validationErrors?.currency}
                disabled
                isRequired
                modern
              />
            </div>
          </div>
        </Form>
      </ModalContent>

      <ModalFooter>
        <Button
          text={getTranslate('balanceManage.modal.buttons.submit')}
          status="primary"
          onClick={handleSubmit}
        />
      </ModalFooter>
    </div>
  );
};

export default connect()(addTranslation(TransferMoney));